import { useEffect, useRef, useState } from 'react';

export interface ScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  type?: 'fade' | 'parallax' | 'both';
  speed?: number; // 0-1, lower is slower
  disableOnMobile?: boolean; // Disable parallax on mobile
}

/**
 * Custom hook for scroll animations
 * Triggers fade-in and/or parallax effects when element comes into view
 * Optimized to minimize forced reflow and layout thrashing
 */
export function useScrollAnimation(options: ScrollAnimationOptions = {}) {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    type = 'fade',
    speed = 0.3, // Slow by default
    disableOnMobile = true,
  } = options;

  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [animationReady, setAnimationReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => window.innerWidth < 1024;
    setIsMobile(checkMobile());
    
    const handleResize = () => setIsMobile(checkMobile());
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Defer animation setup until after page interactive
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => setAnimationReady(true));
    } else {
      setTimeout(() => setAnimationReady(true), 1000);
    }
  }, []);

  useEffect(() => {
    if (!animationReady) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Keep observing for parallax effect
        }
      },
      { threshold, rootMargin }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, rootMargin, animationReady]);

  // Handle parallax effect - OPTIMIZED to avoid forced reflow
  // Only use transform-based parallax (no layout reads on scroll)
  useEffect(() => {
    // Disable parallax on mobile to reduce jank
    if (disableOnMobile && isMobile) return;
    if (type !== 'parallax' && type !== 'both') return;

    let rafId: number;
    let lastScrollY = 0;

    const handleScroll = () => {
      // Use RAF to batch scroll updates
      if (rafId) cancelAnimationFrame(rafId);
      
      rafId = requestAnimationFrame(() => {
        if (isVisible && ref.current) {
          // Calculate parallax offset based on scroll position
          // Use a simple calculation that doesn't require getBoundingClientRect
          const offset = lastScrollY * speed;
          setScrollY(offset);
        }
      });

      lastScrollY = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isVisible, speed, type, isMobile, disableOnMobile]);

  return {
    ref,
    isVisible,
    style: {
      opacity: isVisible ? 1 : 0,
      transform: type === 'parallax' || type === 'both' 
        ? `translateY(${scrollY}px)` 
        : 'translateY(0)',
      transition: isVisible && type !== 'parallax' && type !== 'both'
        ? 'opacity 1.2s ease-out'
        : type === 'both'
        ? 'opacity 1.2s ease-out'
        : 'none',
    },
  };
}
